import { useCallback } from "react";
import { useLanguage } from "../../localisation/useLanguage";

/**
 * Hook providing utility functions for date and time operations
 * Uses the current locale from the language pack for formatting
 */
export const useDateTimeUtils = () => {
    const { languagePack } = useLanguage();
    const locale = languagePack.langIso;

    /**
     * Converts an ISO timestamp string to a localized date and time string
     * @param isoTimestamp - ISO timestamp string (e.g., "2025-04-14T21:21:23.924125")
     * @param options - Optional Intl.DateTimeFormatOptions for customizing the output format
     * @returns Formatted date and time string according to the current locale
     */
    const convertISOToLocaleDateTimeString = useCallback((
        isoTimestamp: string,
        options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }
    ): string => {
        if (!isoTimestamp) return '';
        
        try {
            const date = new Date(isoTimestamp);
            return new Intl.DateTimeFormat(locale, options).format(date);
        } catch (error) {
            console.error('Error formatting date:', error);
            return isoTimestamp;
        }
    }, [locale]);

    /**
     * Converts an ISO timestamp string to a compact localized date string
     * @param isoTimestamp - ISO timestamp string (e.g., "2025-04-14T21:21:23.924125")
     * @returns Formatted date string according to the current locale
     */
    const convertISOToCompactLocaleDate = useCallback((
        isoTimestamp: string
    ): string => {
        return convertISOToLocaleDateTimeString(isoTimestamp, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        });
    }, [convertISOToLocaleDateTimeString]);

    /**
     * Converts an ISO timestamp string to a localized time string
     * @param isoTimestamp - ISO timestamp string (e.g., "2025-04-14T21:21:23.924125")
     * @returns Formatted time string according to the current locale
     */
    const convertISOToLocaleTime = useCallback((
        isoTimestamp: string
    ): string => {
        return convertISOToLocaleDateTimeString(isoTimestamp, {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }, [convertISOToLocaleDateTimeString]);

    return {
        convertISOToLocaleDateTimeString,
        convertISOToCompactLocaleDate,
        convertISOToLocaleTime
    };
};